// ============================================================================
// PERMISSIONS CONFIG — Single source of truth for roles & permissions
// ============================================================================
//
// To add/remove a permission for a role: edit the `rolePermissions` map below.
// To add a new role: add it to `AppRole` and create its entry in `rolePermissions`.
// To add a sidebar item: add it to `sidebarConfig` with the required permission.
// ============================================================================

// ---------------------------------------------------------------------------
// 1. Permission constants — format: "module.action"
// ---------------------------------------------------------------------------

export const Permission = {
  // Clients
  CLIENTS_VIEW: 'clients.view',
  CLIENTS_CREATE: 'clients.create',
  CLIENTS_EDIT: 'clients.edit',
  CLIENTS_DELETE: 'clients.delete',
  CLIENTS_EXPORT: 'clients.export',

  // Points
  POINTS_VIEW: 'points.view',
  POINTS_ADD: 'points.add',
  POINTS_REDEEM: 'points.redeem',
  POINTS_EXPORT: 'points.export',

  // Users (Vendedores)
  USERS_VIEW: 'users.view',
  USERS_CREATE: 'users.create',
  USERS_EDIT: 'users.edit',
  USERS_DELETE: 'users.delete',

  // Branch Offices (Sucursales)
  BRANCHES_VIEW: 'branches.view',
  BRANCHES_CREATE: 'branches.create',
  BRANCHES_EDIT: 'branches.edit',
  BRANCHES_DELETE: 'branches.delete',

  // Benefits (Beneficios)
  BENEFITS_VIEW: 'benefits.view',
  BENEFITS_CREATE: 'benefits.create',
  BENEFITS_EDIT: 'benefits.edit',
  BENEFITS_DELETE: 'benefits.delete',
  BENEFITS_EXPORT: 'benefits.export',
  BENEFITS_BACKFILL: 'benefits.backfill',

  // Vouchers (Vales)
  VOUCHERS_REDEEM: 'vouchers.redeem',
  VOUCHERS_EXPORT: 'vouchers.export',

  // Products
  PRODUCTS_VIEW: 'products.view',

  // Config
  CONFIG_VIEW: 'config.view',
  CONFIG_EDIT: 'config.edit',

  // Emails
  EMAILS_VIEW: 'emails.view',
  EMAILS_SEND: 'emails.send',

  // Cron Logs
  CRON_LOGS_VIEW: 'cron_logs.view',
} as const

export type PermissionKey = (typeof Permission)[keyof typeof Permission]

// ---------------------------------------------------------------------------
// 2. Roles — frontend roles (backend only knows Admin | User)
// ---------------------------------------------------------------------------

export type AppRole = 'Admin' | 'User' | 'Analytics' | 'ERP'

/**
 * Maps the backend role string to a frontend AppRole.
 * The backend only knows 'Admin' and 'User'. If new backend roles are added,
 * extend this mapping accordingly.
 */
export function mapBackendRole(backendRole: string): AppRole {
  if (backendRole === 'Admin') return 'Admin'
  return 'User' // Backend 'User' = Vendedor
}

// ---------------------------------------------------------------------------
// 3. Role → Permissions mapping — THE CENTRAL CONFIG
// ---------------------------------------------------------------------------

export const rolePermissions: Record<AppRole, readonly PermissionKey[]> = {
  // Admin: full access to everything
  Admin: Object.values(Permission),

  // User (Vendedor): day-to-day operations only
  User: [
    // Clients — can view, create, edit but NOT delete or export
    Permission.CLIENTS_VIEW,
    Permission.CLIENTS_CREATE,
    Permission.CLIENTS_EDIT,

    // Points — can view, add, redeem but NOT export
    Permission.POINTS_VIEW,
    Permission.POINTS_ADD,
    Permission.POINTS_REDEEM,

    // Vouchers — can redeem but NOT export
    Permission.VOUCHERS_REDEEM,

    // Products — view only
    Permission.PRODUCTS_VIEW,
  ],

  // Analytics: will be defined when analytics features are built
  Analytics: [],

  // ERP: will be defined when ERP features are built
  ERP: [],
}

// ---------------------------------------------------------------------------
// 4. Sidebar configuration — each item needs a permission to be visible
// ---------------------------------------------------------------------------

export type SidebarIconName =
  | 'BarChart3'
  | 'Users'
  | 'UserPlus'
  | 'Store'
  | 'Package'
  | 'Gift'
  | 'Ticket'
  | 'Settings'
  | 'Mail'
  | 'HelpCircle'
  | 'Clock'

export interface SidebarItemConfig {
  label: string
  path: string
  icon: SidebarIconName
  /** Permission required to see this item. `null` = visible to everyone. */
  permission: PermissionKey | null
}

export const sidebarConfig: SidebarItemConfig[] = [
  { label: 'Puntos', path: '/points', icon: 'BarChart3', permission: Permission.POINTS_VIEW },
  { label: 'Clientes', path: '/clients', icon: 'Users', permission: Permission.CLIENTS_VIEW },
  { label: 'Vendedores', path: '/users', icon: 'UserPlus', permission: Permission.USERS_VIEW },
  { label: 'Sucursales', path: '/branches', icon: 'Store', permission: Permission.BRANCHES_VIEW },
  { label: 'Productos', path: '/products', icon: 'Package', permission: Permission.PRODUCTS_VIEW },
  { label: 'Beneficios', path: '/benefits', icon: 'Gift', permission: Permission.BENEFITS_VIEW },
  { label: 'Canje Vales', path: '/vouchers/redeem', icon: 'Ticket', permission: Permission.VOUCHERS_REDEEM },
  { label: 'Configuraci\u00f3n', path: '/config', icon: 'Settings', permission: Permission.CONFIG_VIEW },
  { label: 'Emails', path: '/emails', icon: 'Mail', permission: Permission.EMAILS_VIEW },
  { label: 'Cron Logs', path: '/cron-logs', icon: 'Clock', permission: Permission.CRON_LOGS_VIEW },
  { label: 'Ayuda', path: '/help', icon: 'HelpCircle', permission: null },
]

// ---------------------------------------------------------------------------
// 5. Route permission mapping — which permission each route requires
// ---------------------------------------------------------------------------

export interface RoutePermissionConfig {
  path: string
  permission: PermissionKey
}

/**
 * Routes that require specific permissions beyond basic authentication.
 * Routes NOT listed here are accessible to any authenticated user.
 */
export const protectedRoutes: RoutePermissionConfig[] = [
  // Users
  { path: '/users', permission: Permission.USERS_VIEW },
  { path: '/users/new', permission: Permission.USERS_CREATE },
  { path: '/users/:id/edit', permission: Permission.USERS_EDIT },

  // Branches
  { path: '/branches', permission: Permission.BRANCHES_VIEW },
  { path: '/branches/new', permission: Permission.BRANCHES_CREATE },
  { path: '/branches/:id/edit', permission: Permission.BRANCHES_EDIT },

  // Benefits
  { path: '/benefits', permission: Permission.BENEFITS_VIEW },
  { path: '/benefits/new', permission: Permission.BENEFITS_CREATE },
  { path: '/benefits/:id', permission: Permission.BENEFITS_VIEW },
  { path: '/benefits/:id/edit', permission: Permission.BENEFITS_EDIT },
  { path: '/benefits/backfill', permission: Permission.BENEFITS_BACKFILL },

  // Config
  { path: '/config', permission: Permission.CONFIG_VIEW },

  // Emails
  { path: '/emails', permission: Permission.EMAILS_VIEW },

  // Cron Logs
  { path: '/cron-logs', permission: Permission.CRON_LOGS_VIEW },
  { path: '/cron-logs/:id', permission: Permission.CRON_LOGS_VIEW },
]
